"use client";

import { $api } from "@/lib/api-client";

/**
 * Global-settings key holding the UVM token-holder count.
 *
 * A vanilla ERC-20 exposes no holder count on-chain (only `balanceOf`), so the
 * number is materialised server-side by the nft-watcher UVM-holder indexer
 * (replays `Transfer` logs, counts non-zero balances) and stored here. The
 * frontend just reads this public setting — no heavy RPC work in the browser.
 */
export const UVM_HOLDER_COUNT_SETTING = "uvm_holder_count";

interface UseUvmHolderCountResult {
  /** Holder count, or `undefined` until the indexer has written the setting. */
  count: number | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** Reads the UVM token-holder count from the public global-settings endpoint. */
export function useUvmHolderCount(): UseUvmHolderCountResult {
  const query = $api.useQuery(
    "get",
    "/settings/{name}",
    { params: { path: { name: UVM_HOLDER_COUNT_SETTING } } },
    // Holder count moves slowly and is refreshed by a server-side job; a long
    // staleTime (react-query option — 4th arg) avoids refetch storms when the
    // invest tab is toggled.
    { staleTime: 5 * 60_000 },
  );

  const raw = query.data?.data?.value;
  const parsed = raw != null ? Number(raw) : Number.NaN;
  const count = Number.isFinite(parsed) ? parsed : undefined;

  return { count, isLoading: query.isLoading, isError: query.isError };
}
